import { Clock, Lock01 } from '@untitledui/icons';

import { DateTimeValueLabel } from '@/lib/crm/components/properties/labels/DateTimeValueLabel';
import type { AssociationType } from '@/lib/crm/core/SchemaClient';
import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';
import type { Note } from '@/lib/crm/record/memo/notes.types';

import { OverviewFieldRow } from '../components/OverviewFieldRow';
import { MemoPrivacyToggle } from '../memo/MemoPrivacyToggle';
import { memoPrivacy } from '../memo/notes.utils';
import { OVERVIEW_TASK_FIELD_ORDER } from './taskFieldOrder';
import { TaskFieldRows } from './TaskFieldRows';

/**
 * Visibility and Last interaction are not click-to-edit cells (a dropdown and
 * a read-only timestamp), so their value boxes differ in height from their
 * labels; centering aligns text to text. The editable rows need no override —
 * InlineEditableFieldRow's cell geometry already lines them up.
 */
const PLAIN_ROW_CLASS = 'items-center';

// Stable no-op for the read-only Visibility row (the control is disabled then).
const NOOP_PRIVACY_CHANGE = () => {};

interface TaskOverviewFieldsProps {
  orgId: number;
  task: Note;
  dueDateSchema: PropertyTypeSchema | undefined;
  /** The task→owner association type; undefined while schema loads. */
  assigneeAssociationType: AssociationType | undefined;
  onStageChange?: (
    entityId: string,
    newStageEntityId: string | null
  ) => Promise<void>;
  onSaveDue?: (newDate: string | null) => Promise<void>;
  onSaveAssignee?: (targetIds: string[]) => Promise<void>;
  /** See TaskFieldRows' `isAssigneeMutationPending`. */
  isAssigneeMutationPending: boolean;
  /**
   * Flip the task's visibility (`true` = shared with the org). Withheld
   * (undefined) when the viewer is neither owner nor creator — the same gate
   * the backend's privacy toggle enforces — which renders the row read-only.
   */
  onPrivacyChange?: (isShared: boolean) => void;
  isPrivacyMutationPending?: boolean;
}

/**
 * The task slideout's "Overview" section body: the editable task fields
 * (TaskFieldRows — the same InlineEditableFieldRow every other slideout's
 * overview uses), then Visibility and Last interaction.
 */
export function TaskOverviewFields({
  orgId,
  task,
  dueDateSchema,
  assigneeAssociationType,
  onStageChange,
  onSaveDue,
  onSaveAssignee,
  isAssigneeMutationPending,
  onPrivacyChange,
  isPrivacyMutationPending = false,
}: TaskOverviewFieldsProps) {
  const privacy = memoPrivacy(task);
  return (
    // Same rhythm as OverviewSection: 12px between rows so one row's 32px
    // hover/edit cell never overlaps the next, and 6px of bleed above the
    // first / below the last.
    <div className='space-y-3 py-1.5'>
      <TaskFieldRows
        order={OVERVIEW_TASK_FIELD_ORDER}
        orgId={orgId}
        taskEntityId={task.entity_id}
        stageEntityId={task.stage_entity_id ?? null}
        ownerEntityId={task.owner_entity_id}
        date={task.date}
        datetime={task.datetime}
        dueDateSchema={dueDateSchema}
        assigneeAssociationType={assigneeAssociationType}
        onStageChange={onStageChange}
        onSaveDue={onSaveDue}
        onSaveAssignee={onSaveAssignee}
        isAssigneeMutationPending={isAssigneeMutationPending}
      />
      <OverviewFieldRow
        icon={Lock01}
        label='Visibility'
        rowClassName={PLAIN_ROW_CLASS}
      >
        {/* Tasks carry the same org/owner visibility model as notes (the
            backend's privacy toggle is memo-kind generic). Read-only for a
            viewer who is neither owner nor creator — the dropdown stays so the
            value reads the same either way. */}
        <MemoPrivacyToggle
          variant='labeled'
          privacy={privacy}
          onChange={onPrivacyChange ?? NOOP_PRIVACY_CHANGE}
          isDisabled={onPrivacyChange === undefined || isPrivacyMutationPending}
        />
      </OverviewFieldRow>
      <OverviewFieldRow
        icon={Clock}
        label='Last interaction'
        rowClassName={PLAIN_ROW_CLASS}
      >
        <DateTimeValueLabel value={task.updated_at || null} />
      </OverviewFieldRow>
    </div>
  );
}
